import { usePostHog } from 'posthog-js/react';
import { setAnalyticsOptOut } from '../../utils/localStorage';

/**
 * The single place a player's analytics opt-out preference is applied.
 * Shared by the entry-screen transparency notice (GDPR.tsx) and the
 * "Privacy choices" panel (TrailMenuDialog) so the two entry points can
 * never diverge.
 *
 * Cookieless/anonymous model: analytics is session-scoped, has no
 * persistent identifier, and defaults ON — this is purely an OPT-OUT
 * control, not a blocking accept/reject gate. Order matters: PostHog's
 * in-session capture state is flipped FIRST, so the choice takes effect
 * immediately for the rest of this session regardless of whether
 * persistence succeeds; the preference is THEN (best-effort) persisted.
 *
 * Returns whether the persist succeeded. Callers must check this — a
 * failed persist on an opt-out must never be treated as "analytics is
 * back on"; it just means the choice won't survive a reload, which the
 * caller should surface to the player.
 */
export default function useConsentChoice() {
  const posthog = usePostHog();

  return (optedOut: boolean): boolean => {
    if (optedOut) {
      posthog?.opt_out_capturing();
    } else {
      posthog?.opt_in_capturing();
    }
    return setAnalyticsOptOut(optedOut);
  };
}
